import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent } from '../../components/ui/Card';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUser, FiMapPin, FiInfo, FiCamera, FiCheckCircle } = FiIcons;

export const SupplierProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('suppliers_1739200000000')
      .select('*')
      .eq('owner_id', user.id)
      .single();

    if (data) setProfile(data);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    const formData = new FormData(e.target);
    const updates = {
      name: formData.get('name'),
      location: formData.get('location'),
      description: formData.get('description'),
      image_url: formData.get('image_url'),
    };

    const { error } = await supabase
      .from('suppliers_1739200000000')
      .update(updates)
      .eq('id', profile.id);

    if (error) {
      setMessage({ type: 'error', text: error.message });
    } else {
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setProfile({ ...profile, ...updates });
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Loading profile...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">Supplier Settings</h1>
        <p className="text-zinc-500">Manage your business information and public profile</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardContent className="p-8 space-y-8">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="relative group">
                <div className="w-32 h-32 rounded-2xl bg-zinc-100 overflow-hidden flex items-center justify-center border-2 border-dashed border-zinc-200">
                  {profile?.image_url ? (
                    <img src={profile.image_url} alt={profile.name} className="w-full h-full object-cover" />
                  ) : (
                    <SafeIcon icon={FiCamera} className="w-8 h-8 text-zinc-400" />
                  )}
                </div>
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-2xl cursor-pointer">
                  <span className="text-white text-xs font-bold">Change Image</span>
                </div>
              </div>

              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                <Input name="name" label="Business Name" defaultValue={profile?.name} required />
                <Input name="location" label="Location" defaultValue={profile?.location} placeholder="City, Country" />
                <Input name="image_url" label="Logo URL" defaultValue={profile?.image_url} placeholder="https://..." />
                <div className="flex items-center gap-2 pt-8">
                  <div className={`w-2 h-2 rounded-full ${profile?.verified ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                  <span className="text-sm font-medium text-zinc-600">
                    {profile?.verified ? 'Verified Partner' : 'Verification Pending'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-zinc-700">Business Description</label>
              <textarea
                name="description"
                rows={4}
                className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl text-sm focus:ring-2 focus:ring-zinc-900 focus:outline-none transition-all"
                defaultValue={profile?.description}
                placeholder="Tell buyers about your farm, products, and values..."
              />
            </div>

            {message.text && (
              <div className={`p-4 rounded-xl text-sm flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiInfo} className="w-4 h-4" />
                {message.text}
              </div>
            )}

            <div className="pt-4 border-t border-zinc-100 flex justify-end">
              <Button type="submit" disabled={saving} className="px-12">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </motion.div>
  );
};